/**
 * Shared document relation resolver.
 *
 * Single source of truth for "what is the parent of this document" between:
 *  - UI hierarchy renderer (SuperfakturaDocumentsTab, QuoteDocumentHierarchy)
 *  - Backend create-final-invoice / sync / backfill / auto-link / webhook
 *
 * Pure TS, no Node or browser APIs. Safe to import from both client and server.
 *
 * Heuristic sources for the parent link, in priority order:
 *   1. canonical: `related_document_id` (DB FK)
 *   2. SF raw payload: `Invoice.estimate_id`, `Invoice.proforma_id`, `Invoice.parent_id`
 *   3. extracted: `extracted_data.metadata.relatedDocumentNumber`
 *   4. fallback: comment / notes containing the parent's document_number
 */

export type RelationDocumentType =
  | "quote"
  | "deposit_invoice"
  | "proforma"
  | "tax_document"
  | "invoice"
  | "contract"
  | "credit_note"
  | "expense"
  | string;

/**
 * Minimal shape needed by the relation resolver. The backend
 * `superfaktura_documents` row and the UI's `LinkedDocument` both satisfy it.
 */
export interface RelationDoc {
  id: string;
  document_type?: RelationDocumentType | null;
  document_number?: string | null;
  document_name?: string | null;
  sf_document_id?: string | null;
  related_document_id?: string | null;
  related_document_number?: string | null;
  raw_data?: any;
  extracted_data?: any;
}

/** True if doc points at the given quote (by id, number, sf_document_id, or any of the heuristic sources). */
export function isDocumentRelatedToQuote(
  doc: RelationDoc,
  quoteId: string,
  quoteDocumentNumber?: string | null,
  quoteSfDocumentId?: string | null
): boolean {
  const relId = doc.related_document_id;
  const relNum = doc.related_document_number;
  const raw = doc.raw_data as any;
  const inv = raw?.Invoice ?? raw?.invoice ?? {};
  const q = raw?.Quote ?? raw?.quote ?? {};
  const estimateId = inv.estimate_id ?? inv.estimateId;
  const proformaId =
    inv.proforma_id ??
    inv.proformaId ??
    inv.parent_id ??
    inv.parentId ??
    q.parent_id ??
    q.parentId;
  const relNumFromRaw =
    relNum ??
    doc.extracted_data?.metadata?.relatedDocumentNumber ??
    (estimateId != null
      ? String(estimateId)
      : proformaId != null
        ? String(proformaId)
        : null);
  const qNum = (quoteDocumentNumber ?? "").trim();
  const qSf = (quoteSfDocumentId ?? "").trim();
  if (!qNum && !qSf) return relId === quoteId;
  const notes =
    inv.comment ??
    inv.Comment ??
    raw?.comment ??
    raw?.Comment ??
    raw?.notes ??
    raw?.Notes ??
    "";
  const notesStr = String(notes || "").toLowerCase();
  const qNumLower = qNum.toLowerCase();
  const inNotes = !!qNum && notesStr.includes(qNumLower);
  return (
    relId === quoteId ||
    relNum === qNum ||
    relNum === qSf ||
    relNumFromRaw === qNum ||
    relNumFromRaw === qSf ||
    (raw?.related_document_id != null &&
      (String(raw.related_document_id) === qSf ||
        String(raw.related_document_id) === qNum)) ||
    (estimateId != null && (String(estimateId) === qSf || String(estimateId) === qNum)) ||
    (proformaId != null && (String(proformaId) === qSf || String(proformaId) === qNum)) ||
    inNotes
  );
}

/** Document flow: quote → deposit_invoice → tax_document/invoice. A deposit can never be child of a tax doc. */
const DEPOSIT_TYPES: ReadonlyArray<RelationDocumentType> = ["deposit_invoice", "proforma"];
const TAX_OR_FINAL_TYPES: ReadonlyArray<RelationDocumentType> = ["tax_document", "invoice"];

/** True if doc is linked to parent (e.g. daňový → zálohová). */
export function isDocumentRelatedToDocument(
  doc: RelationDoc,
  parentId: string,
  parent?: RelationDoc | null
): boolean {
  if (doc.related_document_id === parentId) return true;
  if (!parent) return false;
  // Deposits are never children of tax/final invoices (semantic guard).
  if (
    DEPOSIT_TYPES.includes(doc.document_type as any) &&
    TAX_OR_FINAL_TYPES.includes(parent.document_type as any)
  ) {
    return false;
  }
  const raw = doc.raw_data as any;
  const inv = raw?.Invoice ?? raw?.invoice ?? {};
  const relNum =
    doc.related_document_number ??
    doc.extracted_data?.metadata?.relatedDocumentNumber ??
    (inv.estimate_id ??
      inv.estimateId ??
      inv.proforma_id ??
      inv.proformaId ??
      inv.parent_id ??
      inv.parentId);
  const relNumStr = relNum != null ? String(relNum).trim() : "";
  if (!relNumStr) return false;
  const pNum = parent.document_number ?? "";
  const pSf = parent.sf_document_id ?? "";
  const pName = parent.document_name ?? "";
  if (relNumStr === pNum || relNumStr === pSf || (pName && relNumStr === pName)) return true;
  const numericPart = relNumStr.replace(/^[A-Za-z]+/, "").trim();
  if (numericPart && (numericPart === pNum || numericPart === pSf || (pName && pName.includes(numericPart)))) {
    return true;
  }
  if (pNum && pNum.includes(relNumStr)) return true;
  if (pSf && pSf.includes(relNumStr)) return true;
  return false;
}

/**
 * Confidence score for a discovered link. Higher = more reliable.
 * Used by the backend repair step to decide whether to auto-persist.
 */
export type LinkSource =
  | "related_document_id"
  | "raw_estimate_id"
  | "raw_proforma_id"
  | "raw_parent_id"
  | "extracted_metadata"
  | "related_document_number"
  | "comment_text";

export interface LinkResolution<T extends RelationDoc = RelationDoc> {
  parent: T | null;
  ambiguous: boolean;
  candidates: T[];
  source?: LinkSource;
}

function describeSource(doc: RelationDoc): LinkSource | undefined {
  if (doc.related_document_id) return "related_document_id";
  const raw = doc.raw_data as any;
  const inv = raw?.Invoice ?? raw?.invoice ?? {};
  if (inv.estimate_id != null || inv.estimateId != null) return "raw_estimate_id";
  if (inv.proforma_id != null || inv.proformaId != null) return "raw_proforma_id";
  if (inv.parent_id != null || inv.parentId != null) return "raw_parent_id";
  if (doc.extracted_data?.metadata?.relatedDocumentNumber) return "extracted_metadata";
  if (doc.related_document_number) return "related_document_number";
  return "comment_text";
}

/**
 * Find the parent QUOTE for a deposit/tax/invoice document.
 * Single match → ok. Zero or multiple → ambiguous=true with candidates.
 */
export function findParentQuote<T extends RelationDoc>(
  doc: T,
  allDocs: ReadonlyArray<T>
): LinkResolution<T> {
  const quotes = allDocs.filter((d) => d.document_type === "quote" && d.id !== doc.id);
  const matches = quotes.filter((q) =>
    isDocumentRelatedToQuote(doc, q.id, q.document_number ?? q.sf_document_id, q.sf_document_id)
  );
  if (matches.length === 1) {
    return { parent: matches[0], ambiguous: false, candidates: matches, source: describeSource(doc) };
  }
  if (matches.length === 0) {
    return { parent: null, ambiguous: false, candidates: [] };
  }
  return { parent: null, ambiguous: true, candidates: matches, source: describeSource(doc) };
}

/**
 * Find the parent DEPOSIT (zálohová / proforma) for a tax_document or final invoice.
 * Single match → ok. Zero or multiple → ambiguous=true with candidates.
 */
export function findParentDeposit<T extends RelationDoc>(
  doc: T,
  allDocs: ReadonlyArray<T>
): LinkResolution<T> {
  const deposits = allDocs.filter(
    (d) => (d.document_type === "deposit_invoice" || d.document_type === "proforma") && d.id !== doc.id
  );
  const matches = deposits.filter((dep) => isDocumentRelatedToDocument(doc, dep.id, dep));
  if (matches.length === 1) {
    return { parent: matches[0], ambiguous: false, candidates: matches, source: describeSource(doc) };
  }
  if (matches.length === 0) {
    return { parent: null, ambiguous: false, candidates: [] };
  }
  return { parent: null, ambiguous: true, candidates: matches, source: describeSource(doc) };
}

/**
 * Generic: find an immediate parent for any doc among `allDocs`.
 * Used by the UI hierarchy builder to find depth-2 parents.
 */
export function findParentDocument<T extends RelationDoc>(
  doc: T,
  allDocs: ReadonlyArray<T>
): LinkResolution<T> {
  const candidates = allDocs.filter(
    (d) => d.id !== doc.id && isDocumentRelatedToDocument(doc, d.id, d)
  );
  if (candidates.length === 1) {
    return { parent: candidates[0], ambiguous: false, candidates, source: describeSource(doc) };
  }
  if (candidates.length === 0) {
    return { parent: null, ambiguous: false, candidates: [] };
  }
  return { parent: null, ambiguous: true, candidates, source: describeSource(doc) };
}

/**
 * Find an existing final invoice (Konečná faktúra) attached to the given quote.
 * Used by `create-final-invoice` for idempotency.
 *
 * Looks for `document_type === 'invoice'` rows whose relation walk ultimately
 * resolves to the given quote — either directly, or via a deposit.
 */
export function findFinalInvoiceForQuote<T extends RelationDoc>(
  quote: T,
  allDocs: ReadonlyArray<T>
): T | null {
  const quoteNum = quote.document_number ?? quote.sf_document_id;
  const quoteSf = quote.sf_document_id;
  // direct: invoice → quote
  const direct = allDocs.find(
    (d) =>
      d.id !== quote.id &&
      d.document_type === "invoice" &&
      isDocumentRelatedToQuote(d, quote.id, quoteNum, quoteSf)
  );
  if (direct) return direct;
  // indirect: invoice → deposit → quote
  const deposits = allDocs.filter(
    (d) =>
      d.id !== quote.id &&
      (d.document_type === "deposit_invoice" || d.document_type === "proforma") &&
      isDocumentRelatedToQuote(d, quote.id, quoteNum, quoteSf)
  );
  for (const dep of deposits) {
    const inv = allDocs.find(
      (d) =>
        d.id !== quote.id &&
        d.id !== dep.id &&
        d.document_type === "invoice" &&
        isDocumentRelatedToDocument(d, dep.id, dep)
    );
    if (inv) return inv;
  }
  return null;
}

/**
 * Walk up the chain to find the root quote for any document.
 * tax_document → deposit → quote, or deposit → quote, or invoice → (deposit | quote).
 */
export function findRootQuote<T extends RelationDoc>(
  doc: T,
  allDocs: ReadonlyArray<T>
): T | null {
  if (doc.document_type === "quote") return doc;
  // Direct quote link?
  const direct = findParentQuote(doc, allDocs);
  if (direct.parent) return direct.parent;
  // Walk via deposit
  const dep = findParentDeposit(doc, allDocs);
  if (dep.parent) {
    const viaDeposit = findParentQuote(dep.parent, allDocs);
    if (viaDeposit.parent) return viaDeposit.parent;
  }
  return null;
}
